// src/pages/negative/NegativeListPage.jsx
import React from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Badge,
  Button,
  Card,
  Table,
  TextInput,
  Select,
  Pagination,
  Modal,
  Label,
  Alert,
} from "flowbite-react";
import {
  HiOutlineSearch,
  HiOutlineTrash,
  HiPaperAirplane,
  HiCheckCircle,
  HiShieldCheck,
  HiSparkles,
  HiOutlineX,
  HiOutlineExternalLink,
  HiOutlineEye,
  HiOutlineClock,
} from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { NEGATIVE } from "../../config/apiConfig";
import { format } from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import { RxUpdate } from "react-icons/rx";
import { formatDocument } from "../../utils/utils";
import { useApplication } from "../../contexts/ApplicationContext";
import { Tooltip } from "react-tooltip";
import NegativePromoHero from "../common/NegativePromoHero";

const MOTIVOS_BAIXA = [
  {
    Codigo: "01",
    Motivo: "Pagamento da dívida",
    Descricao: "O devedor pagou a dívida.",
  },
  {
    Codigo: "03",
    Motivo: "Determinação Judicial",
    Descricao: "Baixa da anotação para cumprimento de determinação judicial.",
  },
  {
    Codigo: "05",
    Motivo: "Ordem Depto Jurídico",
    Descricao: "Ordem Depto Jurídico",
  },
  {
    Codigo: "06",
    Motivo: "Documento Roubado/BO",
    Descricao: "Documento Roubado",
  },
  {
    Codigo: "09",
    Motivo: "Falecimento do Devedor",
    Descricao: "Falecimento do Devedor",
  },
  { Codigo: "26", Motivo: "Pagamento Inibido", Descricao: "Pagamento Inibido" },
];

// helpers
const brl = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
const cx = (...xs) => xs.filter(Boolean).join(" ");
const actionWrapClass = "inline-flex items-center gap-1";
const actionBtnBase =
  "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent p-0 transition focus:outline-none focus:ring-2 focus:ring-offset-1";
const actionBtnVariants = {
  neutral:
    "bg-slate-100/80 text-slate-600 hover:bg-slate-200 hover:text-slate-900 focus:ring-slate-300/60",
  info: "bg-blue-100/80 text-blue-700 hover:bg-blue-200 hover:text-blue-800 focus:ring-blue-200/70",
  success:
    "bg-emerald-100/80 text-emerald-700 hover:bg-emerald-200 hover:text-emerald-800 focus:ring-emerald-200/70",
};

const STATUS_LABELS = {
  ABERTO: "Aguardando aprovação",
  ENVIADA: "Em andamento",
  ACEITA: "Sucesso",
  ACEITO: "Sucesso",
  PENDENTE: "Alterações pendentes",
  REJEITADA: "Rejeitada",
  REJEITADO: "Rejeitada",
  BAIXA_SOLICITADA: "Baixa solicitada",
  BAIXADA: "Baixa realizada",
  BAIXADO: "Baixa realizada",
};

const StatusBadge = ({ status, className }) => {
  const S = String(status || "").toUpperCase();
  const map = {
    ABERTO: "warning",
    PENDENTE: "warning",
    ENVIADA: "info",
    BAIXA_SOLICITADA: "info",
    ACEITA: "success",
    ACEITO: "success",
    REJEITADA: "failure",
    REJEITADO: "failure",
    BAIXADA: "gray",
    BAIXADO: "gray",
  };
  const label = STATUS_LABELS[S] || S || "—";
  return (
    <Badge color={map[S] || "indigo"} className={cx("w-fit", className)}>
      {label}
    </Badge>
  );
};

const normalizeStatus = (status) => String(status || "").toUpperCase();
const isSuccessStatus = (status) =>
  ["ACEITA", "ACEITO"].includes(normalizeStatus(status));
const isPendingChangesStatus = (status) =>
  ["PENDENTE", "ABERTO"].includes(normalizeStatus(status));

function useNegatives({
  companyId,
  q,
  status,
  dateFrom,
  dateTo,
  page,
  pageSize,
  orderBy,
  orderDir,
}) {
  return useQuery(
    [
      "negatives",
      {
        companyId,
        q,
        status,
        dateFrom,
        dateTo,
        page,
        pageSize,
        orderBy,
        orderDir,
      },
    ],
    async () => {
      const params = {
        q,
        status,
        dateFrom,
        dateTo,
        page,
        pageSize,
        orderBy,
        orderDir,
        companyId,
      };
      const { data } = await axios.get(NEGATIVE.LIST, { params });
      return data; // { items, total, page, pageSize, totalPages }
    },
    { keepPreviousData: true }
  );
}

function useNegativeDetail(id, open) {
  return useQuery(
    ["negative-detail", id],
    async () => {
      const { data } = await axios.get(NEGATIVE.GET(id));
      return data;
    },
    { enabled: !!id && open }
  );
}

export default function NegativeListPage() {
  const { user } = useApplication();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [orderBy, setOrderBy] = React.useState("id");
  const [orderDir, setOrderDir] = React.useState("DESC");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [statusLoadingIds, setStatusLoadingIds] = React.useState(new Set());
  const isRowLoading = (id) => statusLoadingIds.has(id);
  const activeFiltersCount = React.useMemo(() => {
    let count = 0;
    if (q) count += 1;
    if (status) count += 1;
    if (dateFrom) count += 1;
    if (dateTo) count += 1;
    return count;
  }, [q, status, dateFrom, dateTo]);

  const clearFilters = () => {
    setQ("");
    setStatus("");
    setDateFrom("");
    setDateTo("");
    syncToPage(1);
  };

  const syncToPage = (p) => {
    setCurrentPage(p);
    setPage(p);
  };

  const { data, isLoading } = useNegatives({
    companyId: user?.Company?.id,
    q: q || undefined,
    status: status || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    page,
    pageSize,
    orderBy,
    orderDir,
  });

  React.useEffect(() => {
    if (location.state?.refetch) {
      qc.invalidateQueries("negatives");
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, qc, navigate]);

  // mutations
  const sendExternal = useMutation(
    async (id) => {
      const { data } = await axios.post(NEGATIVE.SEND(id), {});
      return data;
    },
    { onSuccess: () => qc.invalidateQueries("negatives") }
  );

  const baixa = useMutation(
    async ({ id, motivoBaixa }) => {
      const { data } = await axios.post(NEGATIVE.BAIXA(id), {
        motivoBaixa,
      });
      return data;
    },
    {
      onSuccess: (_, { id }) => {
        qc.invalidateQueries("negatives");
        qc.invalidateQueries(["negative-detail", id]);
      },
    }
  );

  const searchStatus = async (negativeId) => {
    try {
      // liga loading para a linha específica
      setStatusLoadingIds((prev) => {
        const next = new Set(prev);
        next.add(negativeId);
        return next;
      });
      await axios.get(`${NEGATIVE.SEARCH_STATUS}/${negativeId}`);
      // você pode fazer um invalidate aqui se a API atualizar o status no backend
      qc.invalidateQueries("negatives");
      qc.invalidateQueries(["negative-detail", negativeId]);
    } catch (error) {
      // opcional: toast/alert de erro
    } finally {
      // desliga loading da linha
      setStatusLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(negativeId);
        return next;
      });
    }
  };

  // modal baixa
  const [openBaixa, setOpenBaixa] = React.useState(false);
  const [baixaId, setBaixaId] = React.useState(null);
  const [motivoCodigo, setMotivoCodigo] = React.useState("");

  const openBaixaModal = (id) => {
    setBaixaId(id);
    setMotivoCodigo("");
    setOpenBaixa(true);
  };

  // modal detalhes
  const [openDetail, setOpenDetail] = React.useState(false);
  const [detailId, setDetailId] = React.useState(null);
  const {
    data: detail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useNegativeDetail(detailId, openDetail);
  const [detailTab, setDetailTab] = React.useState("devedor");
  const openDetailModal = (id, tab = "devedor") => {
    setDetailId(id);
    setDetailTab(tab);
    setOpenDetail(true);
  };

  const statusHistory = React.useMemo(() => {
    const raw =
      detail?.statusHistory ||
      detail?.historicoStatus ||
      detail?.statusHistorico ||
      detail?.statusHistoryEntries;
    return Array.isArray(raw) ? raw : [];
  }, [detail]);

  const resolveHistoryUser = (entry) => {
    if (!entry) return "—";
    return (
      entry?.user?.name ||
      entry?.user?.fullName ||
      entry?.usuario?.name ||
      entry?.usuario?.nome ||
      entry?.responsavel ||
      entry?.responsavelNome ||
      entry?.responsavelName ||
      "—"
    );
  };

  const resolveHistoryDate = (entry) =>
    entry?.createdAt || entry?.date || entry?.data || entry?.created_on;

  const resolveHistoryObservation = (entry) =>
    entry?.observacao || entry?.observation || entry?.note || entry?.notes || "";

  const totalPages = data?.totalPages || 1;

  if (!user?.Company?.hasCV) {
    return (
      <div className="relative">
        <NegativePromoHero priceLabel="A partir de R$ 19,90 por CPF/CNPJ" />
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto">
        <div className="mb-6 flex justify-between gap-2 md:items-center">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
              Negativações
            </h1>
            <p className="text-sm text-gray-500">
              Liste, filtre e gerencie as negativações dos seus clientes.
            </p>
          </div>
          <Button onClick={() => navigate("/negatives/create")}>
            Incluir negativação
          </Button>
        </div>

        {/* Filtros */}
        <div className="mb-4 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-gray-50">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Filtros
              </span>
              {activeFiltersCount > 0 && (
                <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  {activeFiltersCount} ativo(s)
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="xs"
                className="bg-secondary min-w-24"
                onClick={() => syncToPage(1)}
                isProcessing={isLoading}
              >
                <div className="flex items-center gap-1">
                  <HiOutlineSearch /> Buscar
                </div>
              </Button>
              <Button
                color="light"
                size="xs"
                onClick={clearFilters}
                disabled={activeFiltersCount === 0}
              >
                Limpar
              </Button>
            </div>
          </div>

          <div className="px-3 py-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3 rounded-xl border border-slate-200 bg-slate-50/40 p-3">
              <div className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm xl:col-span-3">
                <Label className="mb-1 block text-[13px] font-medium text-slate-600">
                  Busca
                </Label>
                <TextInput
                  icon={HiOutlineSearch}
                  placeholder="Nome, documento, contrato..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>

              <div className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm">
                <Label className="mb-1 block text-[13px] font-medium text-slate-600">
                  Status
                </Label>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="ABERTO">Aguardando aprovação</option>
                  <option value="ENVIADA">Em andamento</option>
                  <option value="ACEITO">Sucesso</option>
                  <option value="PENDENTE">Alterações pendentes</option>
                  <option value="REJEITADO">Rejeitada</option>
                  <option value="BAIXA_SOLICITADA">Baixa solicitada</option>
                  <option value="BAIXADO">Baixa realizada</option>
                </Select>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm">
                <Label className="mb-1 block text-[13px] font-medium text-slate-600">
                  Venc. de
                </Label>
                <TextInput
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>

              <div className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm">
                <Label className="mb-1 block text-[13px] font-medium text-slate-600">
                  Venc. até
                </Label>
                <TextInput
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="border-0">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
            <div>
              <div className="text-sm text-gray-600">
                Total: <span className="font-semibold">{data?.total || 0}</span>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table hoverable className="text-sm">
              <Table.Head className="bg-slate-50">
                <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500">
                  #
                </Table.HeadCell>
                <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500">
                  Devedor
                </Table.HeadCell>
                <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500">
                  Valor
                </Table.HeadCell>
                <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500 text-center">
                  Dt. Inclusão
                </Table.HeadCell>
                <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500 text-center">
                  Birôs
                </Table.HeadCell>
                <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500 text-center">
                  Status
                </Table.HeadCell>
                <Table.HeadCell className="text-[11px] uppercase tracking-wider text-slate-500 text-center">
                  Ações
                </Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {(data?.items || []).map((it) => (
                  <Table.Row key={it.id} className="bg-white hover:bg-slate-50">
                    <Table.Cell className="py-2 font-medium text-gray-900">
                      {it.id}
                    </Table.Cell>
                    <Table.Cell className="py-2 font-medium">
                      <div>{it.nome || "—"}</div>
                      <div className="text-xs text-gray-500">
                        {formatDocument(it.documentoDevedor)}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="py-2">{brl(it.valor)}</Table.Cell>
                    <Table.Cell className="py-2 text-center align-middle">
                      {it.createdAt
                        ? format(new Date(it.createdAt), "dd/MM/yyyy")
                        : "—"}
                    </Table.Cell>
                    <Table.Cell className="py-2 text-center">
                      {it.bureaus ? it.bureaus.join(", ") : "—"}
                    </Table.Cell>
                    <Table.Cell className="py-2">
                      {isRowLoading(it.id) ? (
                        <div className="flex items-center justify-center py-1">
                          <span className="inline-block h-3 w-3 rounded-full border border-gray-300 border-t-transparent animate-spin" />
                        </div>
                      ) : (
                        <StatusBadge status={it.status} className="mx-auto" />
                      )}
                    </Table.Cell>
                    <Table.Cell className="py-2 text-center">
                      <div className={cx("flex items-center justify-center", actionWrapClass)}>
                        {/* Detalhes (ícone de olho) */}
                        <Button
                          size="xs"
                          color="light"
                          onClick={() => openDetailModal(it.id, "devedor")}
                          className={cx(actionBtnBase, actionBtnVariants.neutral)}
                          data-tooltip-id="negTableTips"
                          data-tooltip-content="Detalhes"
                          disabled={isRowLoading(it.id)}
                        >
                          <HiOutlineEye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="xs"
                          color="light"
                          onClick={() => openDetailModal(it.id, "history")}
                          className={cx(actionBtnBase, actionBtnVariants.info)}
                          data-tooltip-id="negTableTips"
                          data-tooltip-content="Histórico"
                          disabled={isRowLoading(it.id)}
                        >
                          <HiOutlineClock className="h-4 w-4" />
                        </Button>
                        {/* Baixa */}
                        {isSuccessStatus(it.status) && !it.dataBaixa && (
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => openBaixaModal(it.id)}
                            className={cx(actionBtnBase, actionBtnVariants.success)}
                            data-tooltip-id="negTableTips"
                            data-tooltip-content="Dar Baixa"
                            disabled={isRowLoading(it.id)}
                          >
                            <HiCheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}

                {!isLoading && (data?.items || []).length === 0 && (
                  <Table.Row>
                    <Table.Cell
                      colSpan={7}
                      className="text-center text-gray-500 py-8"
                    >
                      Nenhum registro encontrado.
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>

          <Tooltip
            id="negTableTips"
            place="top"
            className="z-50"
            style={{ zIndex: 50 }}
          />
          <Tooltip
            id="negTableStatus"
            place="top"
            className="z-50"
            style={{ zIndex: 50 }}
          />

          {/* Paginação */}
          <div className="flex items-center justify-center mt-4">
            <Pagination
              layout="pagination"
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => syncToPage(p)}
              previousLabel=""
              nextLabel=""
              showIcons
            />
          </div>
        </div>

        {/* Modal Baixa */}
        <Modal
          show={openBaixa}
          size="lg"
          onClose={() => setOpenBaixa(false)}
          popup
        >
          <Modal.Header />
          <Modal.Body>
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">
                Dar baixa na negativação
              </h3>

              <div>
                <Label className="mb-1 block">Motivo *</Label>
                <Select
                  value={motivoCodigo}
                  onChange={(e) => setMotivoCodigo(e.target.value)}
                  required
                >
                  <option value="">Selecione um motivo...</option>
                  {MOTIVOS_BAIXA.map((m) => (
                    <option key={m.Codigo} value={m.Codigo}>
                      {m.Codigo} — {m.Motivo}
                    </option>
                  ))}
                </Select>

                {/* bloco de descrição dinâmica */}
                {(() => {
                  const sel = MOTIVOS_BAIXA.find(
                    (m) => m.Codigo === motivoCodigo
                  );
                  if (!sel) return null;
                  return (
                    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="text-xs text-gray-600">
                        Descrição: {sel.Descricao}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="flex justify-end gap-2">
                <Button color="light" onClick={() => setOpenBaixa(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={async () => {
                    const motivoObj = MOTIVOS_BAIXA.find(
                      (m) => m.Codigo === motivoCodigo
                    );
                    if (!motivoObj) return;

                    // envie só o código OU código + motivo (você escolhe)
                    const motivoBaixa = motivoObj.Codigo; // ou `${motivoObj.Codigo} - ${motivoObj.Motivo}`

                    await baixa.mutateAsync({ id: baixaId, motivoBaixa });
                    setOpenBaixa(false);
                  }}
                  isProcessing={baixa.isLoading}
                  disabled={!motivoCodigo}
                >
                  Confirmar
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* Modal Detalhes */}
        <Modal
          show={openDetail}
          size="4xl"
          onClose={() => setOpenDetail(false)}
          className="backdrop-blur-sm"
        >
          <Modal.Header className="py-2">
            <div className="flex items-center gap-2 mt-1">
              <div>
                <div className="text-base font-semibold">
                  {detail
                    ? `#${detail.id} — ${detail.nome || "—"}`
                    : "Carregando..."}
                </div>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body className="pt-2">
            {isDetailLoading && (
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 animate-pulse rounded w-1/3" />
                <div className="h-24 bg-gray-100 animate-pulse rounded" />
                <div className="h-40 bg-gray-100 animate-pulse rounded" />
              </div>
            )}

            {!isDetailLoading && isDetailError && (
              <Alert color="failure">
                Não foi possível carregar os detalhes.
              </Alert>
            )}

            {!isDetailLoading && detail && (
              <div>
                {/* Status + Valor + Vencimento */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">
                      Status
                    </div>
                    <div className="mt-2">
                      <StatusBadge status={detail.status} />
                    </div>
                  </div>
                  <div className="rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-4 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wide text-emerald-700">
                      Valor
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-emerald-700">
                      {brl(detail.valor)}
                    </div>
                  </div>
                  <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-4 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wide text-indigo-700">
                      Vencimento
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-indigo-700">
                      {detail.dataVencimento
                        ? format(new Date(detail.dataVencimento), "dd/MM/yyyy")
                        : "—"}
                    </div>
                  </div>
                </div>

                {/* Abas */}
                <div className="mt-4 mb-4">
                  <div className="border-b border-gray-200 flex gap-2">
                    {[
                      { key: "devedor", label: "Devedor" },
                      { key: "endereco", label: "Endereço" },
                      { key: "contatos", label: "Contatos" },
                      { key: "history", label: "Histórico" },
                    ].map((t) => {
                      const active = detailTab === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setDetailTab(t.key)}
                          className={`px-3 py-2 text-sm font-medium border-b-2 ${
                            active
                              ? "border-indigo-600 text-indigo-700"
                              : "border-transparent text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>

                  {detailTab === "devedor" && (
                    <div className="mt-3 rounded-lg px-2 py-3 shadow-sm border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div className="text-xs text-gray-500">Nome</div>
                          <div className="font-medium">
                            {detail.nome || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Documento</div>
                          <div className="font-mono">
                            {formatDocument(detail.documentoDevedor)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Contrato</div>
                          <div className="font-medium">
                            {detail.contrato || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">
                            Natureza (código)
                          </div>
                          <div className="font-medium">
                            {detail.codigoNatureza || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Criado em</div>
                          <div className="font-medium">
                            {detail.createdAt
                              ? format(
                                  new Date(detail.createdAt),
                                  "dd/MM/yyyy HH:mm"
                                )
                              : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">
                            Atualizado em
                          </div>
                          <div className="font-medium">
                            {detail.updatedAt
                              ? format(
                                  new Date(detail.updatedAt),
                                  "dd/MM/yyyy HH:mm"
                                )
                              : "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {detailTab === "endereco" && (
                    <div className="mt-3 rounded-lg px-2 py-3 shadow-sm border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-gray-500">
                            Logradouro
                          </div>
                          <div className="font-medium">
                            {detail.endereco || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Bairro</div>
                          <div className="font-medium">
                            {detail.bairro || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Cidade</div>
                          <div className="font-medium">
                            {detail.cidade || "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">UF</div>
                          <div className="font-medium">{detail.uf || "—"}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">CEP</div>
                          <div className="font-medium">{detail.cep || "—"}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {detailTab === "contatos" && (
                    <div className="mt-3 rounded-lg px-2 py-3 shadow-sm border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-gray-500">Telefone</div>
                          <div className="font-medium">
                            {detail.ddd && detail.telefone
                              ? `(${detail.ddd}) ${detail.telefone}`
                              : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Email</div>
                          <div className="font-medium">
                            {detail.email || "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {detailTab === "history" && (
                    <div className="mt-3 rounded-lg px-3 py-3 shadow-sm border border-gray-200">
                      {statusHistory.length > 0 ? (
                        <div className="space-y-3">
                          {statusHistory.map((entry, idx) => {
                            const date = resolveHistoryDate(entry);
                            const statusLabel =
                              entry?.status ||
                              entry?.statusNovo ||
                              entry?.novoStatus;
                            const observation =
                              resolveHistoryObservation(entry);
                            return (
                              <div
                                key={entry?.id || `${date || "hist"}-${idx}`}
                                className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm"
                              >
                                <div className="flex flex-wrap items-center gap-2">
                                  <StatusBadge status={statusLabel} />
                                  <span className="text-xs text-slate-500">
                                    {date
                                      ? format(
                                          new Date(date),
                                          "dd/MM/yyyy HH:mm"
                                        )
                                      : "—"}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {resolveHistoryUser(entry)}
                                  </span>
                                </div>
                                {observation && (
                                  <div className="mt-2 text-xs text-slate-600">
                                    {observation}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">
                          Sem histórico disponível.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Ações rápidas dentro do modal */}
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {isPendingChangesStatus(detail.status) && (
                    <Button
                      color="light"
                      onClick={() => {
                        setOpenDetail(false);
                        navigate("/negatives/create", {
                          state: { editId: detail.id },
                        });
                      }}
                    >
                      Re-editar
                    </Button>
                  )}
                  {isSuccessStatus(detail.status) && (
                    <Button onClick={() => openBaixaModal(detail.id)}>
                      Dar baixa
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>

      {/* Tela “Em breve” (quando hasCV) */}
      {/* Mantida no early-return acima */}
    </>
  );
}
